import store from '@/store';

/**
 * Get the Redux store instance.
 * The store is only read inside callers (never at module load), so ESM's live
 * bindings preserve the original lazy-require behavior that avoided circular
 * dependency issues.
 * @returns {object} The Redux store instance.
 */
const getStore = () => store;

/**
 * Get a mapping of document types to their brand values for a specific target.
 * @param {string} [target='passengers'] - The target entity type ('passengers' or 'purchaser').
 * @returns {object} A map of document type keys to brand values.
 */
export const getDoc2BrandMap = (target = 'passengers') => {
  const {
    env: { documentTypes },
  } = getStore().getState().whitelabelConfig;
  const targetDocumentTypes = documentTypes[target];

  const doc2BrandMap = targetDocumentTypes
    ? Object.keys(targetDocumentTypes).reduce(
        (result, key) => ({
          ...result,
          [key]: targetDocumentTypes[key].brand,
        }),
        {},
      )
    : {};

  return doc2BrandMap;
};

/**
 * Get a mapping of brand values to their processor types.
 * @returns {object} A map of brand values to processor types.
 */
export const getBrand2ProcessorMap = () => {
  const {
    env: { documentTypes },
  } = getStore().getState().whitelabelConfig;
  const { passengers: passengersDocumentTypes = {}, purchaser: purchaserDocumentTypes = {} } =
    documentTypes || {};
  const allAvailableDocumentTypes = {
    ...passengersDocumentTypes,
    ...purchaserDocumentTypes,
  };
  const brand2ProcessorMap = Object.keys(allAvailableDocumentTypes).reduce(
    (result, key) => ({
      ...result,
      [allAvailableDocumentTypes[key].brand]: allAvailableDocumentTypes[key].processor,
    }),
    {},
  );
  return brand2ProcessorMap;
};

/**
 * Get the list of document types that should be ignored for the first passenger.
 * @returns {Array} An array of document type values to ignore.
 */
export const getIgnoredDocuments = () => {
  const {
    env: { documentTypes },
  } = getStore().getState().whitelabelConfig;
  const { ignoredDocumentsOnFirstPassenger = [] } = documentTypes;
  return ignoredDocumentsOnFirstPassenger;
};

/**
 * Get available document types filtered by nationality.
 * @returns {Array} An array of document types available for different nationalities.
 */
export const getAvailableDocumentsByNationality = () => {
  const {
    env: { documentTypes },
  } = getStore().getState().whitelabelConfig;
  const { availableDocumentsByNationality = [] } = documentTypes;
  return availableDocumentsByNationality;
};

/**
 * Get all comparable document values for insurance rules.
 * Values can come from forms as brand values or from saved passengers as processor values.
 * @param {string} documentTypeValue - Document type brand or processor value.
 * @returns {string[]} Comparable document aliases.
 */
export const getDocumentTypeAliases = (documentTypeValue = '') => {
  if (!documentTypeValue) return [];

  const brand2ProcessorMap = getBrand2ProcessorMap();
  const aliases = new Set([documentTypeValue]);

  if (brand2ProcessorMap[documentTypeValue]) {
    aliases.add(brand2ProcessorMap[documentTypeValue]);
  }

  Object.entries(brand2ProcessorMap).forEach(([brand, processor]) => {
    if (processor === documentTypeValue) {
      aliases.add(brand);
    }
  });

  return Array.from(aliases);
};

/**
 * Determine if a given document type is eligible to purchase insurance.
 * This reads from whitelabel env documentTypes, same source used for doc maps.
 * If the flag is missing, it defaults to true to avoid blocking existing brands.
 * Supported config flags on each document entry (any of these may be used):
 * - insuranceEligible
 * - allowInsurance
 * - insuranceAllowed
 * - eligibleForInsurance
 *
 * @param {string} documentTypeValue - The selected document type brand or processor value.
 * @returns {boolean} true if insurance is allowed for the document type.
 */
export const validDocumentTypeForInsurance = (documentTypeValue = '') => {
  const { env } = getStore().getState().whitelabelConfig;
  const invalidDocuments = env?.insurance?.invalidDocuments ?? [];
  const documentTypeAliases = getDocumentTypeAliases(documentTypeValue);

  // Provided as array of invalid document brands
  if (Array.isArray(invalidDocuments)) {
    return !documentTypeAliases.some((documentType) => invalidDocuments.includes(documentType));
  }

  // Provided as object map
  if (invalidDocuments && typeof invalidDocuments === 'object') {
    const entries = documentTypeAliases
      .map((documentType) => invalidDocuments[documentType])
      .filter((entry) => entry !== undefined);

    if (!entries.length) return true;

    // false => valid; true or any other present value => invalid
    return entries.every((entry) => entry === false);
  }

  // Fallback: allow by default
  return true;
};

const documentTypeInstructionsi18nKeys = {
  CPF: 'passengers:instructions.cpf_or_cnpj_or_rg',
  CNPJ: 'passengers:instructions.cpf_or_cnpj_or_rg',
  RG: 'passengers:instructions.cpf_or_cnpj_or_rg',
};

/**
 * Get the i18n key for document type instructions.
 * @param {string} [documentType=''] - The document type to get instructions for.
 * @returns {string} The i18n key for the document type instructions, or empty string if not found.
 */
export const getDocumentTypeInstructioni18NKey = (documentType = '') => {
  return documentTypeInstructionsi18nKeys[documentType] || '';
};
